<template>
  <div class="workspace-shell">
    <aside v-if="!isMobile" class="workspace-sidebar">
      <div class="brand-lockup" :aria-label="$t('爱精益工作台')">
        <img class="brand-logo" src="@/assets/logo.png" alt="">
        <div class="brand-copy">
          <strong>ILEAN</strong>
          <span>{{ $t("爱精益工作台") }}</span>
        </div>
      </div>

      <nav class="sidebar-navigation" :aria-label="$t('工作台导航')">
        <p class="navigation-label">{{ $t("工作台") }}</p>
        <button
          v-for="item in menus"
          :key="item.index"
          type="button"
          :class="['navigation-item', { 'navigation-item--active': $route.path === item.index }]"
          :aria-current="$route.path === item.index ? 'page' : null"
          @click="menuItemSelected(item.index)">
          <i :class="item.icon" aria-hidden="true"></i>
          <span>{{ menuTitle(item) }}</span>
        </button>
      </nav>

      <div class="sidebar-user">
        <el-dropdown
          class="user-dropdown"
          trigger="click"
          placement="top-start"
          @command="handleUserMenuCommand">
          <button class="user-menu-trigger" type="button" :aria-label="$t('打开用户菜单')">
            <span class="sidebar-user-row">
              <img :src="avatarUrl" class="sidebar-user-avatar" alt="">
              <span class="sidebar-user-meta">
                <span class="sidebar-user-name">{{ user.name || '—' }}</span>
                <span v-if="user.roleId === 1" class="user-role">{{ $t("企业管理员") }}</span>
              </span>
            </span>
            <i class="el-icon-more user-menu-more" aria-hidden="true"></i>
          </button>
          <el-dropdown-menu slot="dropdown" class="user-action-menu">
            <el-dropdown-item command="settings">
              <i class="el-icon-setting" aria-hidden="true"></i>
              <span>{{ $t("个人设置") }}</span>
            </el-dropdown-item>
            <el-dropdown-item command="switch-company">
              <i class="el-icon-office-building" aria-hidden="true"></i>
              <span>{{ $t("切换企业") }}</span>
            </el-dropdown-item>
            <el-dropdown-item command="logout" divided class="user-action-menu__danger">
              <i class="el-icon-switch-button" aria-hidden="true"></i>
              <span>{{ $t("退出登录") }}</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </aside>

    <div class="workspace-frame">
      <header class="workspace-header">
        <div class="header-leading">
          <button
            v-if="isMobile"
            class="menu-trigger"
            type="button"
            :aria-label="$t('打开导航菜单')"
            @click="drawerVisible = true">
            <i class="el-icon-s-unfold" aria-hidden="true"></i>
          </button>
          <div class="page-heading">
            <h1>{{ currentMenuTitle }}</h1>
          </div>
        </div>

        <button class="company-switcher" type="button" @click="openSwitchCompanyDialog">
          <span class="company-switcher-icon" aria-hidden="true">
            <i class="el-icon-office-building"></i>
          </span>
          <span class="company-switcher-copy">
            <small>{{ $t("当前企业") }}</small>
            <strong>{{ companyName }}</strong>
          </span>
          <i class="el-icon-arrow-down company-switcher-arrow" aria-hidden="true"></i>
        </button>
      </header>

      <main class="workspace-main">
        <router-view :key="viewKey"></router-view>
      </main>
    </div>

    <el-dialog
                :title="$t('切换企业')"
        :visible.sync="switchDialogVisible"
        custom-class="switch-company-dialog"
        width="420px"
        append-to-body>
      <el-form label-width="auto">
        <el-form-item :label="$t('企业')">
          <el-select
              v-model="selectedCompanyId"
              :placeholder="$t('请选择企业')"
              :loading="relationshipsLoading"
              :disabled="relationshipsLoading">
            <el-option
                v-for="item in employmentRelationships"
                :key="item.companyId"
                :label="item.companyName"
                :value="item.companyId"/>
          </el-select>
        </el-form-item>
        <div
            v-if="!relationshipsLoading && employmentRelationships.length === 0"
            class="switch-company-empty">
          {{ $t("暂无可切换的企业") }}
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelSwitchCompany">{{ $t("取消") }}</el-button>
        <el-button
            type="primary"
            :loading="switchingCompany"
            @click="confirmSwitchCompany">{{ $t("确定") }}</el-button>
      </span>
    </el-dialog>

    <el-drawer
      v-if="isMobile"
      :visible.sync="drawerVisible"
      direction="ltr"
      :with-header="false"
      custom-class="mobile-navigation-drawer"
      size="82%">
      <div class="drawer-body">
        <div class="drawer-header">
          <div class="brand-lockup" :aria-label="$t('爱精益工作台')">
            <img class="brand-logo" src="@/assets/logo.png" alt="">
            <div class="brand-copy">
              <strong>ILEAN</strong>
              <span>{{ $t("爱精益工作台") }}</span>
            </div>
          </div>
          <button class="drawer-close" type="button" :aria-label="$t('关闭导航菜单')" @click="drawerVisible = false">
            <i class="el-icon-close" aria-hidden="true"></i>
          </button>
        </div>

        <nav class="sidebar-navigation drawer-navigation" :aria-label="$t('工作台导航')">
          <p class="navigation-label">{{ $t("工作台") }}</p>
          <button
            v-for="item in menus"
            :key="item.index"
            type="button"
            :class="['navigation-item', { 'navigation-item--active': $route.path === item.index }]"
            :aria-current="$route.path === item.index ? 'page' : null"
            @click="menuItemSelected(item.index)">
            <i :class="item.icon" aria-hidden="true"></i>
            <span>{{ menuTitle(item) }}</span>
          </button>
        </nav>

        <div class="drawer-user">
          <el-dropdown
            class="user-dropdown"
            trigger="click"
            placement="top-start"
            @command="handleUserMenuCommand">
            <button class="user-menu-trigger" type="button" :aria-label="$t('打开用户菜单')">
              <span class="sidebar-user-row">
                <img :src="avatarUrl" class="sidebar-user-avatar" alt="">
                <span class="sidebar-user-meta">
                  <span class="sidebar-user-name">{{ user.name || '—' }}</span>
                  <span v-if="user.roleId === 1" class="user-role">{{ $t("企业管理员") }}</span>
                </span>
              </span>
              <i class="el-icon-more user-menu-more" aria-hidden="true"></i>
            </button>
            <el-dropdown-menu slot="dropdown" class="user-action-menu">
              <el-dropdown-item command="settings">
                <i class="el-icon-setting" aria-hidden="true"></i>
                <span>{{ $t("个人设置") }}</span>
              </el-dropdown-item>
              <el-dropdown-item command="switch-company">
                <i class="el-icon-office-building" aria-hidden="true"></i>
                <span>{{ $t("切换企业") }}</span>
              </el-dropdown-item>
              <el-dropdown-item command="logout" divided class="user-action-menu__danger">
                <i class="el-icon-switch-button" aria-hidden="true"></i>
                <span>{{ $t("退出登录") }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { t } from "@/i18n";
import app from "@/App";
import axios from "axios";

export default {
  name: "Main",
  data() {
    return {
      user: {},
      issues: [],
      deadline: null,
      dialogVisible: false,
      page: 1,
      countOfAllIssues: 0,
      searchCriteria: {
        startOfDeadline: '',
        endOfDeadline: '',
      },
      activeIndex: '1',
      activeIndex2: '1',
      menus: [],
      onUserUpdatedHandler: null,
      employmentRelationships: [],
      relationshipsLoading: false,
      switchDialogVisible: false,
      selectedCompanyId: null,
      switchingCompany: false,
      viewKey: 0,
      isMobile: false,
      drawerVisible: false,
      resizeHandler: null,
    }
  },
  computed: {
    companyName() {
      const company = this.user && this.user.company;
      return (company && company.name) ? company.name : '—';
    },
    avatarUrl() {
      const profilePicture = this.user && this.user.profilePicture;
      if (typeof profilePicture === 'string' && profilePicture.trim()) {
        return profilePicture;
      }
      return 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png';
    },
    currentMenuTitle() {
      return this.menuTitle({ index: this.$route.path });
    },
  },
  methods: {
    menuTitle(item) {
      const routeTitles = {
        '/browser/issues': t("问题管理"),
        '/browser/employees': t("员工管理"),
        '/browser/organization-structure': t("组织架构"),
        '/browser/slideshow': t("轮播图管理"),
        '/browser/notifications': t("消息通知"),
        '/browser/profile': t("个人资料"),
        '/browser/issue-rank': t("问题排行"),
        '/browser/issue-stats': t("问题统计"),
        '/browser/employee-list': t("员工活跃度"),
        '/browser/companies': t("企业管理"),
      };
      return routeTitles[item.index] || t(item.title || '工作台');
    },
    openLoginPage() {
      this.$router.replace({path: '/login'})
    },
    logout() {
      app.methods.closeWebSocket();
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
      this.openLoginPage();
    },
    menuItemSelected(key) {
      if (this.isMobile) {
        this.drawerVisible = false;
      }
      if (this.$route.path !== key) {
        this.$router.replace({path: key});
      }
    },
    handleUserMenuCommand(command) {
      if (this.isMobile) {
        this.drawerVisible = false;
      }
      if (command === 'settings') {
        this.menuItemSelected('/browser/profile');
        return;
      }
      if (command === 'switch-company') {
        this.openSwitchCompanyDialog();
        return;
      }
      if (command === 'logout') {
        this.logout();
      }
    },
    async loadUser(options = { connect: false, skipRelationships: false }) {
      const { connect = false, skipRelationships = false } = options || {};
      try {
        const res = await app.methods.getUser();
        this.user = res.data;
        if (!skipRelationships) {
          this.fetchEmploymentRelationships();
        }
        if (connect) {
          app.methods.connectWebSocket();
        }
      } catch (error) {
        const msg = error?.response?.data?.message || t("获取用户信息失败");
        this.$message.error(msg);
        this.openLoginPage();
      }
    },
    async fetchEmploymentRelationships() {
      if (!this.user || !this.user.userId) return;
      this.relationshipsLoading = true;
      try {
        const res = await app.methods.getEmploymentRelationships(this.user.userId);
        this.employmentRelationships = Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        const msg = error?.response?.data?.message || t("获取企业列表失败");
        this.$message.error(msg);
      } finally {
        this.relationshipsLoading = false;
      }
    },
    openSwitchCompanyDialog() {
      if (!this.user || !this.user.userId) {
        this.$message.warning(t("用户信息获取中，请稍后再试"));
        return;
      }
      this.switchDialogVisible = true;
      this.selectedCompanyId = this.user?.company?.companyId || null;
      if (!this.employmentRelationships.length) {
        this.fetchEmploymentRelationships();
      }
    },
    cancelSwitchCompany() {
      this.switchDialogVisible = false;
      this.switchingCompany = false;
    },
    refreshCurrentView() {
      this.viewKey += 1;
    },
    async confirmSwitchCompany() {
      const targetCompanyId = Number(this.selectedCompanyId);
      if (!targetCompanyId) {
        this.$message.warning(t("请选择要切换的企业"));
        return;
      }
      if (this.user?.company?.companyId === targetCompanyId) {
        this.$message.info(t("已在当前企业"));
        this.switchDialogVisible = false;
        return;
      }
      this.switchingCompany = true;
      try {
        await app.methods.switchCompany(targetCompanyId);
        await this.loadUser({ connect: true });
        this.switchDialogVisible = false;
        this.refreshCurrentView();
        this.$message.success(t("已切换企业"));
      } catch (error) {
        const msg = error?.response?.data?.message || t("切换企业失败");
        this.$message.error(msg);
      } finally {
        this.switchingCompany = false;
      }
    },
  },
  created() {
    this.resizeHandler = () => {
      this.isMobile = window.innerWidth <= 900;
      if (!this.isMobile) {
        this.drawerVisible = false;
      }
    };
    this.resizeHandler();
    window.addEventListener('resize', this.resizeHandler);

    this.loadUser({ connect: true });
    this.onUserUpdatedHandler = () => this.loadUser();
    this.$root.$on('user-updated', this.onUserUpdatedHandler);

    axios
      .get(process.env.VUE_APP_BASE_URL + '/menus')
      .then(res => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          this.menus = res.data;
        } else {
          this.$message.error(t("无权访问"));
          this.openLoginPage();
        }
      })
      .catch(err => {
        const msg = err.response?.data?.message || t("无权访问");
        this.$message.error(msg);
        this.openLoginPage();
      })
  },
  beforeDestroy() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.onUserUpdatedHandler) {
      this.$root.$off('user-updated', this.onUserUpdatedHandler);
      this.onUserUpdatedHandler = null;
    }
  },
}
</script>

<style scoped>
.workspace-shell {
  display: flex;
  min-height: 100vh;
  background: var(--page-background);
  color: var(--text-primary);
}

.workspace-sidebar {
  position: sticky;
  top: 0;
  display: flex;
  width: 244px;
  height: 100vh;
  flex: 0 0 244px;
  flex-direction: column;
  overflow: hidden;
  padding: 24px 16px 18px;
  background: #0a3153;
  color: #fff;
}

.brand-lockup {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 0 8px;
}

.brand-logo {
  width: 46px;
  height: 46px;
  flex: 0 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 13px;
  box-shadow: 0 8px 24px rgba(2, 22, 38, 0.22);
}

.brand-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.brand-copy strong {
  color: #fff;
  font-size: 15px;
  letter-spacing: 0.14em;
}

.brand-copy span {
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
  letter-spacing: 0.08em;
}

.sidebar-navigation {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 5px;
  margin-top: 38px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.navigation-label {
  margin: 0 12px 10px;
  color: rgba(255, 255, 255, 0.42);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.16em;
}

.navigation-item {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 46px;
  align-items: center;
  gap: 13px;
  padding: 0 14px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: rgba(255, 255, 255, 0.68);
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.navigation-item i {
  width: 20px;
  flex: 0 0 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 18px;
  text-align: center;
  transition: color 0.2s ease;
}

.navigation-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.navigation-item--active {
  background: rgba(85, 195, 241, 0.16);
  color: #fff;
  font-weight: 600;
}

.navigation-item--active::before {
  position: absolute;
  left: 0;
  width: 3px;
  height: 22px;
  border-radius: 0 3px 3px 0;
  background: #69cef7;
  content: "";
}

.navigation-item--active i {
  color: #69cef7;
}

.sidebar-user,
.drawer-user {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sidebar-user {
  margin-top: 18px;
  padding: 18px 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.sidebar-user-row {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 11px;
}

.sidebar-user-avatar {
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.1);
  object-fit: cover;
}

.sidebar-user-meta {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.sidebar-user-name {
  max-width: 100%;
  overflow: hidden;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-role {
  color: rgba(255, 255, 255, 0.52);
  font-size: 12px;
}

.user-dropdown {
  width: 100%;
}

.user-menu-trigger {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 10px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.user-menu-trigger:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.1);
}

.user-menu-more {
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.58);
  font-size: 18px;
}

.workspace-frame {
  min-width: 0;
  flex: 1;
}

.workspace-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  min-height: 76px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 13px 190px 13px 30px;
  border-bottom: 1px solid var(--border-light);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(18px);
}

.header-leading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.page-heading {
  min-width: 0;
}

.page-heading h1 {
  margin: 0;
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
}

.company-switcher {
  display: flex;
  min-width: 0;
  max-width: 330px;
  align-items: center;
  gap: 10px;
  padding: 7px 12px 7px 8px;
  border: 1px solid var(--border-light);
  border-radius: 13px;
  background: var(--surface-color);
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.company-switcher:hover {
  border-color: var(--primary-color);
  background: var(--surface-hover);
  box-shadow: 0 8px 22px var(--shadow-color);
}

.company-switcher-icon {
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: var(--primary-soft);
  color: var(--primary-color);
  font-size: 17px;
}

.company-switcher-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.company-switcher-copy small {
  color: var(--text-muted);
  font-size: 11px;
}

.company-switcher-copy strong {
  max-width: 220px;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-switcher-arrow {
  flex: 0 0 auto;
  color: var(--text-muted);
  font-size: 12px;
}

.workspace-main {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  max-width: 1680px;
  margin: 0 auto;
  padding: 26px 30px 44px;
}

.menu-trigger,
.drawer-close {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--border-light);
  border-radius: 11px;
  background: var(--surface-color);
  color: var(--text-regular);
  font-size: 19px;
  cursor: pointer;
}

.drawer-body {
  display: flex;
  box-sizing: border-box;
  height: 100%;
  flex-direction: column;
  padding: 20px 16px 18px;
  background: #0a3153;
  color: #fff;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.drawer-close {
  border-color: rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.drawer-navigation {
  margin-top: 34px;
}

.drawer-user {
  margin-top: 18px;
  padding: 18px 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.switch-company-empty {
  padding-left: 80px;
  color: var(--text-muted);
  font-size: 13px;
}

.navigation-item:focus-visible,
.user-menu-trigger:focus-visible,
.company-switcher:focus-visible,
.menu-trigger:focus-visible,
.drawer-close:focus-visible {
  outline: 3px solid rgba(105, 206, 247, 0.35);
  outline-offset: 2px;
}

html[data-theme='dark'] .workspace-header {
  background: rgba(23, 28, 38, 0.9);
}

@media (max-width: 900px) {
  .workspace-header {
    min-height: 64px;
    gap: 10px;
    padding: 10px 178px 10px 14px;
  }

  .page-heading h1 {
    max-width: 110px;
    overflow: hidden;
    font-size: 17px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .company-switcher {
    max-width: min(48vw, 230px);
    padding: 5px 8px 5px 6px;
    border-radius: 11px;
  }

  .company-switcher-icon {
    width: 32px;
    height: 32px;
  }

  .company-switcher-copy small,
  .company-switcher-arrow {
    display: none;
  }

  .company-switcher-copy strong {
    max-width: 100%;
    font-size: 12px;
  }

  .workspace-main {
    padding: 16px 14px 34px;
  }
}

@media (max-width: 560px) {
  .workspace-header {
    flex-wrap: wrap;
    padding: 12px 14px;
  }
  .header-leading {
    width: calc(100% - 164px);
    min-height: 40px;
  }
  .workspace-header > .company-switcher {
    flex-basis: 100%;
    max-width: 100%;
  }
}

@media (max-width: 430px) {
  .page-heading h1 {
    max-width: 86px;
  }

  .company-switcher {
    max-width: 42vw;
  }

  .company-switcher-icon {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .navigation-item,
  .company-switcher {
    transition: none;
  }
}
</style>
